/**
 * Le persone di un'azienda, per la linguetta «Persone» del dettaglio azienda (23/10/2026): da qui il super utente
 * entra nell'account di una di loro («Entra come», sessione assistita di `generateImpersonationToken`).
 *
 * Due letture, unite: chi ha l'azienda fra le iscrizioni (`membership_tenant_ids`) e chi la ha come principale
 * (`azienda_id`, i profili più vecchi senza iscrizioni). Il ruolo SU QUELL'AZIENDA lo dice la regola condivisa
 * (`risolviRuoloSulTenant`), la stessa dell'anteprima dei destinatari della conoscenza: non una seconda.
 */
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { risolviRuoloSulTenant, type UtenteTenantLike } from '@shared/admin/utentiPerTenant';

export interface PersonaDellAzienda {
  uid: string;
  nome: string;
  email: string | null;
  ruoli: string[];
  /** Lavora lì adesso: profilo non disattivato e iscrizione non chiusa. */
  attiva: boolean;
  /** Un super utente: nel suo account non si entra (il server lo rifiuta comunque). */
  super_utente: boolean;
}

const STATI_NON_OPERATIVI = new Set(['disabilitato', 'disabled', 'sospeso', 'suspended']);
const testo = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

export async function personeDellAzienda(tenantId: string): Promise<PersonaDellAzienda[]> {
  const utenti = collection(db, 'app_users');
  const [iscritti, principali] = await Promise.all([
    getDocs(query(utenti, where('membership_tenant_ids', 'array-contains', tenantId))),
    getDocs(query(utenti, where('azienda_id', '==', tenantId))),
  ]);
  const visti = new Map<string, PersonaDellAzienda>();
  for (const d of [...iscritti.docs, ...principali.docs]) {
    if (visti.has(d.id)) continue;
    const dati = d.data() as Record<string, unknown>;
    const iscrizione = (Array.isArray(dati.tenant_memberships) ? (dati.tenant_memberships as Array<Record<string, unknown>>) : [])
      .find((m) => m.tenant_id === tenantId);
    const email = testo(dati.email) || null;
    visti.set(d.id, {
      uid: d.id,
      nome: testo(dati.display_name) || testo(dati.nome_completo) || email || d.id,
      email,
      ruoli: risolviRuoloSulTenant(dati as UtenteTenantLike, tenantId).roles,
      attiva: !STATI_NON_OPERATIVI.has(testo(dati.status).toLowerCase()) && iscrizione?.attiva !== false,
      super_utente: dati.is_super_admin === true,
    });
  }
  return [...visti.values()].sort((a, b) => a.nome.localeCompare(b.nome, 'it', { sensitivity: 'base' }));
}
